import { ReactElement, useMemo, useCallback } from 'react';
import { Stack, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { useFormikContext } from 'formik';

import { useToggle } from '~hooks/useToggle';
// eslint-disable-next-line max-len
import { useGenerateRoutesChargeIndicatorsData } from '~hooks/useGenerateRoutesChargeIndicatorsData';
import { hasError } from '~utils/formHelpers';

import { AssistantGenerateRoutesDialogCapabilitiesInfo } from '../../../../../styles';
import { getSelectedEntityDescription } from '../../../../../utils/helpers';
import {
  AssistantGenerateRoutesDialogCollapsableCard,
  AssistantGenerateRoutesDialogScheduleRouteCardForm,
} from '../../../../../Components';

import { useAssistantGenerateRoutesDialogByTemplateContext } from '../../../../Context';
import {
  FORM_BY_TEMPLATE_FIELDS_NAME,
  getFormByTemplateInitialValues,
  getItemsBySelectedTemplate,
  getItemsId,
  getVehicleBySelectedTemplate,
  getPointDescription,
} from '../../../../utils';

// eslint-disable-next-line max-len
import AssistantGenerateRoutesDialogByTemplateContentFormTemplateSelectedDataItemsTable from './ItemsTable';
// eslint-disable-next-line max-len
import AssistantGenerateRoutesDialogByTemplateContentFormTemplateSelectedDataVehicleTable from './VehicleTable';
// eslint-disable-next-line max-len
import AssistantGenerateRoutesDialogByTemplateContentFormTemplateSelectedDataPreAssignedDriverCard from './PreAssignedDriverCard';

const AssistantGenerateRoutesDialogByTemplateContentFormTemplateSelectedData =
  (): ReactElement => {
    const {
      data: {
        selectedTemplate: { value: selectedTemplate },
        warehouses,
      },
      form: {
        isScheduleRouteEnabled: { setValue: setContextIsScheduleRouteEnabled },
      },
    } = useAssistantGenerateRoutesDialogByTemplateContext();

    const {
      touched,
      errors,
      values,
      getFieldProps,
      setFieldValue,
      setFieldTouched,
    } = useFormikContext<ReturnType<typeof getFormByTemplateInitialValues>>();

    const originPointDescription = useMemo(
      () =>
        getPointDescription({
          pointId: selectedTemplate?.apiTransaction.originId ?? null,
          pointAddress: selectedTemplate?.apiTransaction.originAddress ?? null,
          warehouses,
        }),
      [selectedTemplate, warehouses],
    );

    const destinyPointDescription = useMemo(
      () =>
        getPointDescription({
          pointId: selectedTemplate?.apiTransaction?.destinyId ?? null,
          pointAddress:
            selectedTemplate?.apiTransaction?.destinyAddress ?? null,
          warehouses,
        }),
      [selectedTemplate, warehouses],
    );

    const scheduleStartDateValue = useMemo(
      () => values[FORM_BY_TEMPLATE_FIELDS_NAME.SCHEDULE_START_DATE],
      [values],
    );

    const handleChangeScheduleStartDate = useCallback(
      (value: Date | null): void => {
        setFieldValue(FORM_BY_TEMPLATE_FIELDS_NAME.SCHEDULE_START_DATE, value);
      },
      [setFieldValue],
    );

    const handleBlurScheduleStartDate = useCallback((): void => {
      setFieldTouched(FORM_BY_TEMPLATE_FIELDS_NAME.SCHEDULE_START_DATE, true);
    }, [setFieldTouched]);

    const handleEnabledScheduleRoute = useCallback(
      (isEnabled: boolean) => {
        setContextIsScheduleRouteEnabled(isEnabled);
      },
      [setContextIsScheduleRouteEnabled],
    );

    const [expandedItemsCard, toggleExpandedItemsCard] = useToggle(false);
    const [expandedVehicleCard, toogleExpandedVehicleCard] = useToggle(true);

    const items = useMemo(
      () => getItemsBySelectedTemplate(selectedTemplate),
      [selectedTemplate],
    );

    const itemsId = useMemo(() => getItemsId(items), [items]);

    const vehicles = useMemo(() => {
      const currentVehicle = getVehicleBySelectedTemplate(selectedTemplate);

      if (!currentVehicle) return [];

      return [currentVehicle];
    }, [selectedTemplate]);

    const chargeIndicators = useGenerateRoutesChargeIndicatorsData({
      selectedOrders: items,
      selectedVehicles: vehicles,
    });

    return (
      <Stack spacing={2}>
        <TextField
          {...getFieldProps(FORM_BY_TEMPLATE_FIELDS_NAME.SCHEDULE_NAME)}
          label="Nombre de ruta"
          error={hasError(
            touched,
            errors,
            FORM_BY_TEMPLATE_FIELDS_NAME.SCHEDULE_NAME,
          )}
          fullWidth
          autoComplete="off"
        />

        <Stack direction="row" spacing={2}>
          <TextField
            label="Punto de partida"
            defaultValue={originPointDescription}
            fullWidth
            disabled
          />

          <TextField
            label="Punto de llegada"
            defaultValue={destinyPointDescription}
            fullWidth
            disabled
          />

          <DatePicker
            label="Fecha de inicio"
            value={scheduleStartDateValue}
            onChange={handleChangeScheduleStartDate}
            renderInput={(props) => (
              <TextField
                {...props}
                onBlur={handleBlurScheduleStartDate}
                error={hasError(
                  touched,
                  errors,
                  FORM_BY_TEMPLATE_FIELDS_NAME.SCHEDULE_START_DATE,
                )}
                fullWidth
              />
            )}
          />
        </Stack>

        <Stack>
          <AssistantGenerateRoutesDialogScheduleRouteCardForm
            onEnabled={handleEnabledScheduleRoute}
            startDate={scheduleStartDateValue}
          />
        </Stack>

        <Stack>
          <AssistantGenerateRoutesDialogCollapsableCard
            expanded={expandedItemsCard}
            onExpanded={() => toggleExpandedItemsCard()}
            title="PEDIDOS PROGRAMADOS"
            description={getSelectedEntityDescription(itemsId, 'pedido', false)}
            indicator={
              <AssistantGenerateRoutesDialogCapabilitiesInfo
                capacity1={chargeIndicators.orders.capacity1}
                capacity2={chargeIndicators.orders.capacity2}
                numberOfPackages={chargeIndicators.orders.numberOfPackages}
                maximumVisits={null}
                fontSize={14}
                fontWeight="bold"
                autoFlow="row"
                rows={1}
                columns={3}
              />
            }
          >
            <AssistantGenerateRoutesDialogByTemplateContentFormTemplateSelectedDataItemsTable />
          </AssistantGenerateRoutesDialogCollapsableCard>
        </Stack>

        <Stack>
          <AssistantGenerateRoutesDialogCollapsableCard
            expanded={expandedVehicleCard}
            onExpanded={() => toogleExpandedVehicleCard()}
            title="VEHÍCULO PROGRAMADO"
            indicator={
              <AssistantGenerateRoutesDialogCapabilitiesInfo
                capacity1={chargeIndicators.vehicles.capacity1}
                capacity2={chargeIndicators.vehicles.capacity2}
                maximumVisits={null}
                fontSize={14}
                fontWeight="bold"
                autoFlow="row"
                rows={1}
                columns={2}
              />
            }
          >
            <AssistantGenerateRoutesDialogByTemplateContentFormTemplateSelectedDataVehicleTable />
          </AssistantGenerateRoutesDialogCollapsableCard>
        </Stack>

        <Stack>
          {/* eslint-disable-next-line max-len */}
          <AssistantGenerateRoutesDialogByTemplateContentFormTemplateSelectedDataPreAssignedDriverCard />
        </Stack>
      </Stack>
    );
  };

export default AssistantGenerateRoutesDialogByTemplateContentFormTemplateSelectedData;
